import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Check, X, Globe, RefreshCw,
  Pencil, FlaskConical, Clock,
} from 'lucide-react';
import { PageHeader, SectionCard, StatusBadge } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { api } from '../api';

const statusVariantMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  draft: 'neutral',
  pending_review: 'warning',
  approved: 'info',
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
};

export default function AdminProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['adminProducts', 'products']);
  const { toasts, addToast, removeToast } = useToast();

  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [auditLog, setAuditLog] = useState<any[]>([]);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishInput, setPublishInput] = useState('');

  const fetchProduct = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await api.getAdminVendorProduct(id);
      setProduct(result.data);
    } catch {
      addToast('Failed to load product', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLog = async () => {
    if (!id) return;
    try {
      const result = await api.getAdminProductAuditLog(id);
      setAuditLog(result.data || []);
    } catch { /* non-critical */ }
  };

  useEffect(() => { fetchProduct(); fetchAuditLog(); }, [id]);

  const handleApprove = async () => {
    if (!id) return;
    try {
      const result = await api.approveVendorProduct(id);
      if (result.shopify_error) addToast('Approved but Shopify push failed', 'warning');
      else addToast('Product approved', 'success');
      fetchProduct();
      fetchAuditLog();
    } catch { addToast('Failed to approve', 'error'); }
  };

  const handleReject = async () => {
    if (!id) return;
    try {
      await api.rejectVendorProduct(id, rejectNotes);
      addToast('Product rejected', 'success');
      setRejectOpen(false);
      setRejectNotes('');
      fetchProduct();
      fetchAuditLog();
    } catch { addToast('Failed to reject', 'error'); }
  };

  const handleSyncShopify = async () => {
    if (!id) return;
    setSyncing(true);
    try {
      const result = await api.syncAdminVendorProductToShopify(id);
      addToast(t('detail.syncSuccess') + ` (${result.data.action})`, 'success');
      fetchProduct();
      fetchAuditLog();
    } catch {
      addToast(t('detail.syncFailed'), 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handlePublish = async () => {
    if (!id || !publishInput.trim()) return;
    const ids = publishInput.split(',').map(s => s.trim()).filter(Boolean);
    try {
      await api.publishVendorProduct(id, ids);
      addToast('Published', 'success');
      setPublishOpen(false);
      setPublishInput('');
      fetchProduct();
      fetchAuditLog();
    } catch { addToast('Failed to publish', 'error'); }
  };

  const handleUnpublish = async () => {
    if (!id || !product?.published_storefront_ids?.length) return;
    try {
      await api.unpublishVendorProduct(id, product.published_storefront_ids.map(String));
      addToast('Unpublished', 'success');
      fetchProduct();
      fetchAuditLog();
    } catch { addToast('Failed to unpublish', 'error'); }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-white/5 rounded" />
          <div className="h-64 bg-white/5 rounded" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-5xl mx-auto">
        <PageHeader title="Product not found" />
        <Link to="/admin/products" className="btn-secondary text-sm">{t('detail.backToProducts')}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate('/admin/products')} className="flex items-center gap-1 text-xs text-white/40 hover:text-white/70 mb-3 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('detail.backToProducts')}
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-display font-bold text-white">{product.title}</h1>
            <StatusBadge variant={statusVariantMap[product.status] || 'neutral'}>
              {t(`products:status.${product.status}`)}
            </StatusBadge>
            {product.is_test && (
              <span className="text-[10px] px-1.5 py-0.5 text-white/40 bg-white/5 rounded flex items-center gap-1" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                <FlaskConical className="w-3 h-3" /> TEST
              </span>
            )}
          </div>
          <p className="text-sm text-white/40 mt-1">{product.vendor_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {product.status === 'pending_review' && (
            <>
              <button onClick={handleApprove} className="btn-primary flex items-center gap-1.5 text-sm bg-[#4ade80] hover:bg-[#4ade80]/80 text-[#0B0B0B]">
                <Check className="w-3.5 h-3.5" /> {t('detail.approve')}
              </button>
              <button onClick={() => setRejectOpen(true)} className="btn-secondary flex items-center gap-1.5 text-sm text-[#f87171]">
                <X className="w-3.5 h-3.5" /> {t('detail.reject')}
              </button>
            </>
          )}
          <Link to={`/admin/products/${id}/edit`} className="btn-secondary flex items-center gap-1.5 text-sm">
            <Pencil className="w-3.5 h-3.5" /> {t('detail.edit')}
          </Link>
        </div>
      </div>

      {/* Product Info */}
      <SectionCard title={t('detail.productInfo')}>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-white/40 text-xs uppercase">{t('detail.titleEn')}</span>
              <p className="text-white/80">{product.title || '—'}</p>
            </div>
            <div>
              <span className="text-white/40 text-xs uppercase">{t('detail.titleZh')}</span>
              <p className="text-white/80">{product.title_zh || '—'}</p>
            </div>
            <div className="col-span-2">
              <span className="text-white/40 text-xs uppercase">{t('detail.description')}</span>
              <p className="text-white/60 text-xs mt-1">{product.description || '—'}</p>
            </div>
            <div>
              <span className="text-white/40 text-xs uppercase">{t('detail.category')}</span>
              <p className="text-white/80">{t(`products:categories.${product.category}`)}</p>
            </div>
            <div>
              <span className="text-white/40 text-xs uppercase">{t('detail.tags')}</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {(product.tags || []).map((tag: string) => (
                  <span key={tag} className="text-[10px] px-1.5 py-0.5 text-white/40 bg-white/5 rounded">{tag}</span>
                ))}
                {(!product.tags || product.tags.length === 0) && <span className="text-white/20 text-xs">—</span>}
              </div>
            </div>
            <div>
              <span className="text-white/40 text-xs uppercase">{t('detail.priceRange')}</span>
              <p className="text-white/80">{product.price_min != null ? `$${product.price_min}` : '—'}{product.price_max != null && product.price_max !== product.price_min ? ` – $${product.price_max}` : ''}</p>
            </div>
            <div>
              <span className="text-white/40 text-xs uppercase">{t('detail.moq')}</span>
              <p className="text-white/80">{product.moq ?? '—'}</p>
            </div>
            {product.lead_time_days != null && (
              <div>
                <span className="text-white/40 text-xs uppercase">{t('detail.leadTime')}</span>
                <p className="text-white/80">{product.lead_time_days} {t('detail.days')}</p>
              </div>
            )}
          </div>

          {/* Images */}
          {product.images?.length > 0 && (
            <div>
              <span className="text-white/40 text-xs uppercase block mb-2">{t('detail.images')}</span>
              <div className="flex gap-2 flex-wrap">
                {product.images.map((img: any) => (
                  <div key={img.id} className="w-20 h-20 rounded overflow-hidden bg-white/5 shrink-0">
                    <img src={img.image_url} alt={img.alt_text || ''} referrerPolicy="no-referrer" className="w-full h-full object-cover"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Variants */}
          {product.variants?.length > 0 && (
            <div>
              <span className="text-white/40 text-xs uppercase block mb-2">{t('detail.variants')}</span>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left px-3 py-2 text-white/30">SKU</th>
                    <th className="text-left px-3 py-2 text-white/30">Title</th>
                    <th className="text-left px-3 py-2 text-white/30">Price</th>
                    <th className="text-left px-3 py-2 text-white/30">Inventory</th>
                  </tr>
                </thead>
                <tbody>
                  {product.variants.map((v: any) => (
                    <tr key={v.id} className="border-b border-white/[0.03]">
                      <td className="px-3 py-2 text-white/60">{v.sku || '—'}</td>
                      <td className="px-3 py-2 text-white/60">{v.title || 'Default'}</td>
                      <td className="px-3 py-2 text-white/60">{v.price != null ? `$${v.price}` : '—'}</td>
                      <td className="px-3 py-2 text-white/60">{v.inventory_quantity ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Shopify Sync */}
      <SectionCard title={t('detail.shopifySync')}>
        <div className="p-5 flex items-center justify-between gap-4">
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-white/40">{t('detail.shopifyProductId')}:</span>
              {product.shopify_product_id ? (
                <span className="text-white/70 font-mono text-xs">{product.shopify_product_id}</span>
              ) : (
                <span className="text-white/20">{t('shopifyNotSynced')}</span>
              )}
            </div>
            {product.shopify_status && (
              <div className="flex items-center gap-2">
                <span className="text-white/40">{t('detail.shopifyStatus')}:</span>
                <span className="text-white/70 capitalize">{product.shopify_status}</span>
              </div>
            )}
          </div>
          <button
            onClick={handleSyncShopify}
            disabled={syncing}
            className="btn-secondary flex items-center gap-1.5 text-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? t('detail.syncing') : product.shopify_product_id ? t('detail.resyncShopify') : t('detail.syncToShopify')}
          </button>
        </div>
      </SectionCard>

      {/* Storefront Publishing */}
      <SectionCard title={t('detail.storefrontPublishing')}>
        <div className="p-5 space-y-3">
          {product.published_storefront_ids?.length > 0 ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Globe className="w-4 h-4 text-[#4ade80]" />
              <span className="text-sm text-white/60">{t('detail.publishedTo')}:</span>
              {product.published_storefront_ids.map((sid: number) => (
                <span key={sid} className="text-xs px-2 py-0.5 bg-[#4ade80]/10 text-[#4ade80] rounded">{sid}</span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-white/30">{t('detail.noStorefronts')}</p>
          )}
          <div className="flex gap-2">
            {product.shopify_product_id && (
              <button onClick={() => setPublishOpen(true)} className="btn-secondary text-xs flex items-center gap-1">
                <Globe className="w-3 h-3" /> {t('detail.publish')}
              </button>
            )}
            {product.published_storefront_ids?.length > 0 && (
              <button onClick={handleUnpublish} className="btn-ghost text-xs text-[#f87171]">{t('detail.unpublish')}</button>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Activity Log */}
      <SectionCard title={t('detail.activityLog')}>
        <div className="p-5">
          {auditLog.length === 0 ? (
            <p className="text-sm text-white/30">{t('detail.noActivity')}</p>
          ) : (
            <div className="space-y-2">
              {auditLog.map((entry: any, i: number) => (
                <div key={i} className="flex items-start gap-3 text-xs">
                  <Clock className="w-3 h-3 text-white/20 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-white/60">{t(`audit.${entry.action}`, { defaultValue: entry.action })}</span>
                    <span className="text-white/25 ml-2">{new Date(entry.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      {/* Reject Modal */}
      {rejectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={() => setRejectOpen(false)} />
          <div className="card p-6 w-full max-w-md relative z-10">
            <h3 className="text-lg font-display font-bold text-white mb-4">{t('detail.reject')}</h3>
            <textarea
              value={rejectNotes}
              onChange={e => setRejectNotes(e.target.value)}
              className="input w-full min-h-[80px] mb-4"
              placeholder={t('detail.rejectionPlaceholder')}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setRejectOpen(false)} className="btn-ghost">{t('csv.cancel')}</button>
              <button onClick={handleReject} className="btn-primary bg-[#f87171] hover:bg-[#f87171]/80">{t('detail.reject')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Modal */}
      {publishOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={() => setPublishOpen(false)} />
          <div className="card p-6 w-full max-w-md relative z-10">
            <h3 className="text-lg font-display font-bold text-white mb-4">{t('detail.publishToStorefronts')}</h3>
            <p className="text-sm text-white/50 mb-3">{t('detail.publishHint')}</p>
            <input
              value={publishInput}
              onChange={e => setPublishInput(e.target.value)}
              className="input w-full mb-4"
              placeholder="12345, 67890"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setPublishOpen(false)} className="btn-ghost">{t('csv.cancel')}</button>
              <button onClick={handlePublish} className="btn-primary">{t('detail.publish')}</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
